import * as React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Box, H2, Text } from '@openfin/ui-library';

import { SettingsToggle } from '../SettingsToggle/SettingsToggle';
import { PositionSelector, QuadSelector } from '../PositionSelector/PositionSelector';
import {
  ToggleCenterLocked,
  ToggleCenterMuted,
  UpdateAppMuteState,
  UpdateStreamMuteState,
} from '../../../store/Actions';
import { RootState } from '../../../store/State';
import {
  CenterPosition,
  SetCenterPosition,
  SetToastDuration,
  SetToastPosition,
  ToastPosition,
} from '../../../store/partials/Settings';
import { StoredApplication } from '../../../model/Environment';
import { SETTINGS_APPLICATIONS_COLLAPSED_SIZE } from '../../../constants';
import { StoredStream } from '../../../model/StoredStream';
import { NumberSpinner } from '../NumberSpinner/NumberSpinner';

interface Props extends ReturnType<typeof mapDispatch> {
  loggedIn: boolean;
  appManagers: ApplicationManager[];
  centerLocked: boolean;
  centerMuted: boolean;
  centerPosition: CenterPosition;
  toastPosition: ToastPosition;
  toastDuration: number;
}

type ApplicationManager = {
  application: StoredApplication;
  streams?: StoredStream[];
};

const SettingsViewComponent = ({
  appManagers,
  centerLocked,
  centerMuted,
  centerPosition,
  toastPosition,
  setToastPosition,
  setCenterPosition,
  toggleCenterLocked,
  toggleCenterMuted,
  updateAppMuteState,
  updateStreamMuteState,
  toastDuration,
  setToastDuration,
}: Props): JSX.Element => {
  const [isExpanded, setExpanded] = React.useState(false);

  function flipAppMuteState(application: StoredApplication, streams: StoredStream[] = []) {
    const newMuteState = !application.muted;
    updateAppMuteState(application, newMuteState);

    if (streams.length > 0) {
      streams.forEach((stream) => updateStreamMuteState(stream, newMuteState));
    }
  }

  function flipStreamMuteState(stream: StoredStream) {
    updateStreamMuteState(stream, !stream.muted);
  }

  const count =
    appManagers.length > SETTINGS_APPLICATIONS_COLLAPSED_SIZE && !isExpanded
      ? SETTINGS_APPLICATIONS_COLLAPSED_SIZE
      : appManagers.length;

  const visibleAppManagers: ApplicationManager[] = appManagers.slice(0, count);

  return (
    <Container flexDirection="column">
      <PanelSection>
        <PanelSectionTitle>Notification Center</PanelSectionTitle>
        <SettingsToggle
          title="Auto-hide Center"
          state={!centerLocked}
          onChange={toggleCenterLocked}
          toggleId="lock-link"
        />
        <SettingsToggle
          title="Do Not Disturb"
          state={centerMuted}
          onChange={toggleCenterMuted}
          toggleId="mute-link"
        />
        <PositionSelector
          title="Position"
          id="center-position"
          value={centerPosition}
          onChange={setCenterPosition}
        />
      </PanelSection>
      <PanelSection>
        <PanelSectionTitle>Notification Toasts</PanelSectionTitle>
        <TitledSettingContainer alignItems="center" flexDirection="row">
          <Text id="spinner-label" size="large" aria-hidden="true">
            Duration
          </Text>
          <NumberSpinner
            className="spinner"
            value={(toastDuration / 1000) | 0}
            suffix="sec"
            min={4}
            max={16}
            onChange={(duration) => setToastDuration(duration * 1000)}
          />
        </TitledSettingContainer>

        <QuadSelector
          title="Position"
          id="toast-position"
          value={toastPosition}
          onChange={setToastPosition}
        />
      </PanelSection>
      <PanelSection>
        <PanelSectionTitle>Manage Applications </PanelSectionTitle>
        {appManagers.length > 0 &&
          visibleAppManagers.map(({ application, streams = [] }, index) => {
            return (
              <div key={`SettingsToggle-${index}`}>
                <SettingsToggle
                  key={`SettingsToggle-Application-${index}`}
                  iconUrl={application.iconUrl}
                  state={!application.muted}
                  title={application.title}
                  onChange={() => flipAppMuteState(application, streams)}
                  toggleId={`mute-app-toggle-${application.id}`}
                  showLogo
                />
                {streams.length > 0 && (
                  <StreamToggles>
                    {streams?.map((stream: StoredStream, streamIndex) => {
                      return (
                        <SettingsToggle
                          key={`SettingsToggle-Stream-${streamIndex}`}
                          state={!stream.muted}
                          title={stream.displayName}
                          onChange={() => flipStreamMuteState(stream)}
                          toggleId={`mute-stream-toggle-${stream.id}`}
                          subToggle
                        />
                      );
                    })}
                  </StreamToggles>
                )}
              </div>
            );
          })}
        {appManagers.length > SETTINGS_APPLICATIONS_COLLAPSED_SIZE && (
          <ToggleListLimit id="show-less-more-button" onClick={() => setExpanded(!isExpanded)}>
            {isExpanded ? 'Show Fewer' : 'Show More'}
          </ToggleListLimit>
        )}
      </PanelSection>
    </Container>
  );
};

const Container = styled(Box)`
  color: ${({ theme }) => theme.palette.textDefault};
  overflow-x: hidden;
  overflow-y: auto;
  user-select: none;
  height: 100%;
`;

const PanelSection = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.background4};
  margin: ${({ theme }) => `${theme.px.large} 0 0`};
  padding: ${({ theme }) => `0 ${theme.px.large} ${theme.px.large}`};
  flex-direction: column;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const PanelSectionTitle = styled(H2)`
  margin-bottom: ${({ theme }) => theme.px.base};
`;

const ToggleListLimit = styled(Text)`
  cursor: pointer;
  text-align: center;
  margin-top: ${({ theme }) => theme.px.small};

  &:hover {
    text-decoration: underline;
  }
`;

const StreamToggles = styled(Box)`
  border-left: 1px solid ${({ theme }) => theme.palette.background4};
  padding-left: ${({ theme }) => theme.px.xlarge};
  margin-left: calc(${({ theme }) => theme.px.base} - 1px); /* Offset 1px border */
  flex-direction: column;
  margin-bottom: 1rem;
`;

// TODO: Replace SettingToggle with this
const TitledSettingContainer = styled(Box)`
  margin-bottom: ${({ theme }) => theme.px.small};

  & > ${Text} {
    margin-right: auto;
  }

  .spinner {
    // Min size so it doesn't change size on smaller numbers
    min-width: 115px;
  }
`;

const mapStateToProps = (state: RootState) => {
  const appManagers: ApplicationManager[] = Array.from(
    state.applications.values()
  ).map((application) => ({ application, streams: [] }));

  // Attach all streams to their source applications for the UI
  state.streams.forEach((stream) => {
    const appManager = appManagers.find((manager) => manager.application.id === stream.appId);

    if (appManager) {
      appManager.streams?.push(stream);
    }
  });

  return {
    appManagers,
    centerLocked: state.centerLocked,
    centerMuted: state.centerMuted,
    centerPosition: state.settings.centerPosition,
    toastPosition: state.settings.toastPosition,
    toastDuration: state.settings.toastDuration,
    loggedIn: state.loggedIn,
  };
};

const mapDispatch = (dispatch: Dispatch) => ({
  toggleCenterLocked: () => dispatch(new ToggleCenterLocked()),
  toggleCenterMuted: () => dispatch(new ToggleCenterMuted()),
  setCenterPosition: (position: CenterPosition) => dispatch(new SetCenterPosition(position)),
  setToastPosition: (position: ToastPosition) => dispatch(new SetToastPosition(position)),
  updateAppMuteState: (app: StoredApplication, state: boolean) =>
    dispatch(new UpdateAppMuteState(app, state)),
  updateStreamMuteState: (stream: StoredStream, state: boolean) =>
    dispatch(new UpdateStreamMuteState(stream, state)),
  setToastDuration: (duration: number) => dispatch(new SetToastDuration(duration)),
});

export const SettingsView = connect(mapStateToProps, mapDispatch)(SettingsViewComponent);
